import * as React from 'react';
import calculateColumnIndexFromPoint from '../../store/calculation/calculateColumnIndexFromPoint';
import type CalendarSurfaceStore from '../../store/schema/CalendarSurfaceStore';
import { addDays } from 'owa-datetime';
import type { CalendarSurfaceTransformProps } from '../../interfaces/CalendarSurfaceTransformProps';

export interface AllDayWellHooksProps {
    config: CalendarSurfaceTransformProps;
    store: CalendarSurfaceStore;
    calendarFolderId?: string;
}

export default function useAllDayWellHooks(props: AllDayWellHooksProps) {
    const pointToDate = React.useCallback(
        (evt: React.MouseEvent<HTMLElement>, isRTL: boolean) => {
            let boundingRect = evt.currentTarget.getBoundingClientRect();
            let colIndex = calculateColumnIndexFromPoint(
                evt.pageX,
                boundingRect,
                props.config.columnProvider,
                isRTL
            );

            let startDate = props.config.columnProvider.getColumnDate(colIndex);
            let endDate = addDays(startDate, 1);
            return {
                start: startDate,
                end: endDate,
            };
        },
        [props.config.columnProvider]
    );

    const onContextMenu = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => {
            let { onSurfaceRightClick } = props.config;

            if (onSurfaceRightClick) {
                ev.persist();
                let dateRange = pointToDate(ev, props.store.userOptions.isRTL);
                onSurfaceRightClick(
                    dateRange.start,
                    dateRange.end,
                    true,
                    ev.nativeEvent,
                    props.calendarFolderId
                );
                ev.stopPropagation();
                ev.preventDefault();
            }
        },
        [
            props.config.onSurfaceRightClick,
            props.store.userOptions.isRTL,
            props.calendarFolderId,
            pointToDate,
        ]
    );

    const onDoubleClick = React.useCallback(
        (evt: React.MouseEvent<HTMLElement>) => {
            let { onSurfaceDoubleClick } = props.config;

            if (onSurfaceDoubleClick) {
                let dateRange = pointToDate(evt, props.store.userOptions.isRTL);
                onSurfaceDoubleClick(
                    dateRange.start,
                    dateRange.end,
                    true,
                    false /* No click and drag scenario */,
                    props.calendarFolderId
                );
            }
        },
        [
            props.config.onSurfaceDoubleClick,
            props.store.userOptions.isRTL,
            props.calendarFolderId,
            pointToDate,
        ]
    );

    const onClick = React.useCallback(
        (evt: React.MouseEvent<HTMLElement>) => {
            let { onSurfaceClick } = props.config;
            if (onSurfaceClick) {
                let dateRange = pointToDate(evt, props.store.userOptions.isRTL);
                onSurfaceClick(
                    dateRange.start,
                    dateRange.end,
                    true,
                    false /* No click and drag scenario */,
                    props.calendarFolderId
                );
            }
        },
        [
            props.config.onSurfaceClick,
            props.store.userOptions.isRTL,
            props.calendarFolderId,
            pointToDate,
        ]
    );

    return {
        children: null,
        handlers: {
            onContextMenu: onContextMenu,
            onClick: onClick,
            onDoubleClick: onDoubleClick,
        },
    };
}
